/**
 * @voltxampere/web — Window-level shortcut binding (Sprint 11).
 *
 * `attachShortcutHandlers` wires a single `keydown` listener to
 * the window, plus a Preact-Signals effect that flips the
 * manager's active scope as modals open / close. Returns a
 * dispose function for tests / HMR.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect } from "@preact/signals-core";

import { commandPaletteOpen, shortcutOverlayOpen } from "../store/ui.js";
import { shortcuts } from "./index.js";

export function attachShortcutHandlers(target: Window | Document = window): () => void {
  const handler = (e: KeyboardEvent): void => {
    shortcuts.handleKeyDown(e);
  };

  target.addEventListener("keydown", handler as EventListener);

  // Modal scope sync — when ANY modal flag is true, set scope
  // to `modal`; revert otherwise. Prevents single-letter symbol
  // arm shortcuts from firing while the user is searching the
  // command palette.
  const disposeScope = effect(() => {
    const anyOpen = commandPaletteOpen.value || shortcutOverlayOpen.value;
    shortcuts.setScope(anyOpen ? "modal" : "global");
  });

  return () => {
    target.removeEventListener("keydown", handler as EventListener);
    disposeScope();
  };
}
